import { DateRange } from 'rsuite/esm/DateRangePicker';
import { LabelValueType } from '../../../../components/AsyncFormSelect/AsyncFormSelect';
import { RiskSegment } from '../../../../components/Tooltips/RiskSegment/RiskSegmentIndicator';

export interface SportActivityFilterModel {
  DatePlaced?: DateRange | null;
  DateClosed?: DateRange | null;
  EventTypes?: LabelValueType;
  FranchiseIds?: LabelValueType[];
  SportIds?: LabelValueType[];
  CountryIds?: LabelValueType[];
  TournamentIds?: LabelValueType[];
  EventId?: number;
  BetStatus?: LabelValueType;
  BetTypes?: LabelValueType[];
  Legs?: number;
  BetBuilderFilter?: LabelValueType;
  IsEarlyPayout?: LabelValueType;
  FeedProviderIds?: LabelValueType[];
  BetOrigins?: LabelValueType;
  BonusTypes?: LabelValueType[];
  UniqueFranchiseIds?: LabelValueType;
  PlayerId?: number;
  RowCountFrom?: number;
  RowCountTo?: number;
  CurrencyId?: LabelValueType;
  IsTest? : LabelValueType;
  RiskSegmentUuids?: LabelValueType[];
  UtcDateTime?: boolean;
}
export interface SportActivityReportBody {
  DatePlacedFrom?: string;
  DatePlacedTo?: string;
  DateClosedFrom?: string;
  DateClosedTo?: string;
  EventTypes?: number[];
  FranchiseIds?: number[];
  SportIds?: number[];
  CountryIds?: number[];
  TournamentIds?: number[];
  EventId?: number;
  BetStatus?: number;
  BetTypes?: number[];
  BetBuilderFilter?: number;
  IsEarlyPayout?: boolean;
  FeedProviderIds?: number[];
  BetOrigins?: string[];
  BonusTypes?: number[];
  UniqueFranchiseIds?: number[];
  PlayerId?: number;
  RowCountFrom?: number;
  RowCountTo?: number;
  CurrencyId?: number;
  IsTest?: boolean;
  RiskSegmentUuids?: string[];
  HasRiskSegments?: boolean;
}

export interface SportActivityReport {
  Name: string | number;
  GroupId: string;
  Turnover: number;
  BetsCount: number;
  Ggr: number;
  AvgBet: number;
  Margin: number;
  OpenBetsTurnover: number;
  CurrencyId: number;
  PlayerId: number;
  IsLive?: boolean;
  BonusesCost: number;
  RiskSegments?: RiskSegment[];
  CompanyId?: string;
  PlayerUuid?: string;
  TournamentId?: number;
  TournamentName?: string;
  SportId?: number;
  SportName?: string;
  UnionId?: string;
  FranchiseId?: number;
}

export interface SportActivityReportResponse {
  data: SportActivityReport[];
  success: boolean;
}

export enum SAR_REPORT_EVENT_TYPES {
  BOTH = 0,
  PREMATCH = 1,
  LIVE = 2,
}

const SAR_EVENT_TYPE_BOTH: LabelValueType = {
  label: 'Both',
  value: SAR_REPORT_EVENT_TYPES.BOTH.toString(),
};

const SAR_EVENT_TYPE_PREMATCH: LabelValueType = {
  label: 'Prematch',
  value: SAR_REPORT_EVENT_TYPES.PREMATCH.toString(),
};

const SAR_EVENT_TYPE_LIVE: LabelValueType = {
  label: 'Live',
  value: SAR_REPORT_EVENT_TYPES.LIVE.toString(),
};

export const SAR_EVENT_TYPES: LabelValueType[] = [
  SAR_EVENT_TYPE_BOTH,
  SAR_EVENT_TYPE_PREMATCH,
  SAR_EVENT_TYPE_LIVE,
];

export enum LIVE_PREMATCH_BET_TYPES {
  ALL = 1,
  PREMATCH = 2,
  INPLAY = 3
}

export const livePrematchBetTypes: LabelValueType[] = [
  {
    label: 'Prematch',
    value: LIVE_PREMATCH_BET_TYPES.PREMATCH.toString(),
  },
  {
    label: 'Live',
    value: LIVE_PREMATCH_BET_TYPES.INPLAY.toString(),
  },
  {
    label: 'All',
    value: LIVE_PREMATCH_BET_TYPES.ALL.toString(),
  },
];

export enum SportReportTypes {
  Player = 'Player',
  Sport = 'Sport',
  Tournament = 'Tournament',
  Event = 'Event',
  Market = 'Market',
  Bet = 'Bet',
  BookABetCode = 'Book a bet',
  BetOrigin = 'Bet Origin',
}

export enum SAR_BONUS_TYPES {
  REAL_MONEY = 0,
  BONUS = 1,
  FREE_BET = 2,
  NO_RISK_BET = 3,
  YAKATA = 4,
  ONE_CUT_BONUS = 5,
  SUPER_CHARGED_ODDS_BONUS = 6,
  EXTERNAL_BONUS = 7,
  ODD_BOOSTED = 8,
}

const SAR_BONUS_TYPES_REAL_MONEY: LabelValueType = {
  label: 'Real Money',
  value: SAR_BONUS_TYPES.REAL_MONEY.toString(),
};

const SAR_BONUS_TYPES_BONUS: LabelValueType = {
  label: 'Bonus',
  value: SAR_BONUS_TYPES.BONUS.toString(),
};

const SAR_BONUS_TYPES_FREE_BET: LabelValueType = {
  label: 'Free Bet',
  value: SAR_BONUS_TYPES.FREE_BET.toString(),
};

const SAR_BONUS_TYPES_NO_RISK_BET: LabelValueType = {
  label: 'No Risk Bet',
  value: SAR_BONUS_TYPES.NO_RISK_BET.toString(),
};

const SAR_BONUS_TYPES_YAKATA: LabelValueType = {
  label: 'Accumulator',
  value: SAR_BONUS_TYPES.YAKATA.toString(),
};

const SAR_BONUS_TYPES_ONE_CUT_BONUS: LabelValueType = {
  label: 'One Cut Bonus',
  value: SAR_BONUS_TYPES.ONE_CUT_BONUS.toString(),
};

const SAR_BONUS_TYPES_SUPER_CHARGED_ODDS_BONUS: LabelValueType = {
  label: 'Super Charged Odds',
  value: SAR_BONUS_TYPES.SUPER_CHARGED_ODDS_BONUS.toString(),
};

const SAR_BONUS_TYPES_EXTERNAL_BONUS: LabelValueType = {
  label: 'External Bonus',
  value: SAR_BONUS_TYPES.EXTERNAL_BONUS.toString(),
};

const SAR_BONUS_TYPES_ODD_BOOSTED: LabelValueType = {
  label: 'Boosted Odds',
  value: SAR_BONUS_TYPES.ODD_BOOSTED.toString(),
};

export const sportReportBonusTypes: LabelValueType[] = [
  SAR_BONUS_TYPES_REAL_MONEY,
  SAR_BONUS_TYPES_BONUS,
  SAR_BONUS_TYPES_FREE_BET,
  SAR_BONUS_TYPES_NO_RISK_BET,
  SAR_BONUS_TYPES_YAKATA,
  SAR_BONUS_TYPES_ONE_CUT_BONUS,
  SAR_BONUS_TYPES_SUPER_CHARGED_ODDS_BONUS,
  SAR_BONUS_TYPES_EXTERNAL_BONUS,
  SAR_BONUS_TYPES_ODD_BOOSTED,
];

export interface FilteredTournamentRequestBody {
  SportIds: string;
  CountryIds: string;
}

export enum SAR_IS_TEST {
  TRUE = '1',
  FALSE = '2',
}

const SAR_IS_TEST_TRUE: LabelValueType = {
  label: 'True',
  value: SAR_IS_TEST.TRUE,
};

const SAR_IS_TEST_FALSE: LabelValueType = {
  label: 'False',
  value: SAR_IS_TEST.FALSE,
};

export const SAR_IS_TEST_OPTIONS = [
  SAR_IS_TEST_TRUE,
  SAR_IS_TEST_FALSE,
];

export enum SAR_BET_TYPES {
  ALL = '0',
  SINGLES = '1',
  MULTIBETS ='2',
  SYSTEM = '3',
  FOLDS = '4'
}

export const SAR_BET_TYPES_ALL: LabelValueType = {
  label: 'All',
  value: SAR_BET_TYPES.ALL,
};

export const SAR_BET_TYPES_SINGLES: LabelValueType = {
  label: 'Singles',
  value: SAR_BET_TYPES.SINGLES,
};

export const SAR_BET_TYPES_MULTIBETS: LabelValueType = {
  label: 'Multibets',
  value: SAR_BET_TYPES.MULTIBETS,
};

export const SAR_BET_TYPES_SYSTEM: LabelValueType = {
  label: 'System',
  value: SAR_BET_TYPES.SYSTEM,
};

export const SAR_BET_TYPES_FOLDS: LabelValueType = {
  label: 'Folds',
  value: SAR_BET_TYPES.FOLDS,
};

export const SARbetTypes: LabelValueType[] = [
  SAR_BET_TYPES_ALL,
  SAR_BET_TYPES_SINGLES,
  SAR_BET_TYPES_MULTIBETS,
  SAR_BET_TYPES_SYSTEM,
  SAR_BET_TYPES_FOLDS,
];

export enum SAR_GROUP_IDS {
  GRAND_TOTAL = '-1',
}

export const reportTypeToNumber: Record<SportReportTypes, number> = {
  [SportReportTypes.Player]: 0,
  [SportReportTypes.Sport]: 1,
  [SportReportTypes.Tournament]: 2,
  [SportReportTypes.Event]: 3,
  [SportReportTypes.Market]: 4,
  [SportReportTypes.Bet]: 5,
  [SportReportTypes.BookABetCode]: 6,
  [SportReportTypes.BetOrigin]: 7,
};

export const reportTypes: Record<SportReportTypes, string> = {
  [SportReportTypes.Player]: 'Player',
  [SportReportTypes.Sport]: 'Sport',
  [SportReportTypes.Tournament]: 'Tournament',
  [SportReportTypes.Event]: 'Event',
  [SportReportTypes.Market]: 'Market',
  [SportReportTypes.Bet]: 'Bet',
  [SportReportTypes.BookABetCode]: 'BookABetCode',
  [SportReportTypes.BetOrigin]: 'BetOrigin',
};
